import { useRef } from 'react'

import { useToast } from '@/components/ui/toastContext'
import type { ApplicationDocumentType } from '@/features/recruiter/api/applicationDocuments.api'
import {
  useApplicationDocumentsQuery,
  useDeleteApplicationDocument,
  useDownloadApplicationDocument,
  useUploadApplicationDocument,
} from '@/features/recruiter/hooks/useApplicationDocuments'
import { getErrorMessage } from '@/utils/errors'

const DOC_TYPES: { value: ApplicationDocumentType; label: string; icon: string }[] = [
  { value: 'offer_letter', label: 'Offer Letter', icon: 'ti-file-certificate' },
  { value: 'joining_letter', label: 'Joining Letter', icon: 'ti-file-check' },
]

function formatBytes(bytes: number | null): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function HiredDocumentUpload({ applicationId }: { applicationId: number }) {
  const toast = useToast()
  const { data: docs, isPending } = useApplicationDocumentsQuery(applicationId)
  const upload = useUploadApplicationDocument(applicationId)
  const download = useDownloadApplicationDocument(applicationId)
  const deleteDoc = useDeleteApplicationDocument(applicationId)
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const handleFileChange = (docType: ApplicationDocumentType, file: File | null) => {
    if (!file) return
    upload.mutate({ documentType: docType, file }, {
      onSuccess: () => toast.success('Document uploaded successfully.'),
      onError: (err) => toast.error(getErrorMessage(err)),
    })
    // reset the input so the same file can be re-uploaded after delete
    const input = inputRefs.current[docType]
    if (input) input.value = ''
  }

  const handleDelete = (documentId: number) => {
    if (!confirm('Delete this document?')) return
    deleteDoc.mutate(documentId, {
      onSuccess: () => toast.success('Document deleted.'),
      onError: (err) => toast.error(getErrorMessage(err)),
    })
  }

  return (
    <div className="mt-3 pt-3 border-top">
      <p className="fs-13 fw-semibold mb-2 text-success">
        <i className="ti ti-trophy me-1" />
        Candidate Hired — Upload Documents
      </p>

      {isPending ? (
        <div className="text-muted fs-12">
          <span className="spinner-border spinner-border-sm me-1" />
          Loading…
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {DOC_TYPES.map((type) => {
            const existing = (docs ?? []).find((d) => d.documentType === type.value)
            return (
              <div key={type.value} className="d-flex align-items-center justify-content-between p-2 rounded border bg-light gap-2">
                <div className="d-flex align-items-center gap-2 flex-grow-1 min-width-0">
                  <span className="avatar avatar-sm avatar-rounded bg-success-transparent flex-shrink-0">
                    <i className={`ti ${type.icon} text-success fs-13`} />
                  </span>
                  <div className="min-width-0">
                    <p className="fs-13 fw-medium mb-0">{type.label}</p>
                    {existing ? (
                      <p className="fs-11 text-muted mb-0 text-truncate" title={existing.originalName}>
                        {existing.originalName}
                        {existing.fileSize ? ` · ${formatBytes(existing.fileSize)}` : ''}
                      </p>
                    ) : (
                      <p className="fs-11 text-muted mb-0">Not uploaded</p>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-1 flex-shrink-0">
                  {existing ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        title="Download"
                        disabled={download.isPending}
                        onClick={() => download.mutate(existing.id)}
                      >
                        <i className="ti ti-download fs-13" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        title="Delete and re-upload"
                        disabled={deleteDoc.isPending}
                        onClick={() => handleDelete(existing.id)}
                      >
                        <i className="ti ti-trash fs-13" />
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="d-none"
                        ref={(el) => { inputRefs.current[type.value] = el }}
                        onChange={(e) => handleFileChange(type.value, e.target.files?.[0] ?? null)}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={upload.isPending}
                        onClick={() => inputRefs.current[type.value]?.click()}
                      >
                        {upload.isPending ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <><i className="ti ti-upload fs-13 me-1" />Upload</>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
